import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronRight, ChevronLeft, X, Sparkles, Cpu, Flame, Wrench,
  Bot, MessageSquare, Rocket, CheckCircle2, Play,
} from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import LaunchPromoBanner from "@/components/LaunchPromoBanner";
import TOUR_TR from "@/i18n_tour";

// Static (per-step) visual + icon + try-route metadata, independent of language.
// Indexed by step number (0..6).
const META = [
  { icon: Sparkles },
  { icon: Cpu,         tryLink: "/calculators/orbital" },
  { icon: Flame,       tryLink: "/calculators/tig" },
  { icon: Wrench,      tryLink: "/calculators/pipefitter" },
  { icon: Bot,         tryLink: "/ai-advisor",         visualKey: "ai" },
  { icon: MessageSquare, tryLink: "/community",        visualKey: "community" },
  { icon: Rocket,      showPromo: true },
];

const VisualOrbital = () => (
  <div className="grid grid-cols-3 gap-3 text-center">
    {[
      { l: "DN", v: "85" },
      { l: "Thickness / Hrúbka", v: "2.0 mm" },
      { l: "Material", v: "316L" },
    ].map((c, i) => (
      <div key={i} className="border border-gold-700/30 p-3">
        <div className="text-[10px] font-mono uppercase text-zinc-500">{c.l}</div>
        <div className="text-2xl font-display font-black text-gold-400 mt-1">{c.v}</div>
      </div>
    ))}
    <div className="col-span-3 mt-2 border-t border-zinc-800 pt-4 grid grid-cols-4 gap-3 text-center">
      {[
        { l: "Current", v: "85A" },
        { l: "Speed", v: "75 mm/min" },
        { l: "Gas", v: "Ar 8 l/min" },
        { l: "Sector", v: "30°" },
      ].map((c, i) => (
        <div key={i}>
          <div className="text-[9px] font-mono uppercase text-zinc-500">{c.l}</div>
          <div className="text-sm font-display font-bold text-white mt-1">{c.v}</div>
        </div>
      ))}
    </div>
  </div>
);

const VISUALS_BY_LANG = {
  sk: {
    ai: (
      <div className="space-y-3">
        <div className="bg-zinc-900 border border-zinc-800 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Ty</div>
          <div className="text-zinc-200">Mám póry v koreni nerez DN50, 316L, orbitálka Axxair. Čo s tým?</div>
        </div>
        <div className="bg-gold-950/20 border border-gold-700/40 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-gold-500 mb-1">🛠️ Sauberino</div>
          <div className="text-zinc-200 leading-relaxed">
            Fíha doľava — póry v koreni, klasika ktorá zožerie nervy. Tri vinníci, vždy tí istí: 1) plyn — vlhko v hadici alebo nečistý argón, 2) málo back-purge — daj aspoň 5 l/min, 3) mastný materiál — pred zvarom acetón a netkaná handra...
          </div>
        </div>
      </div>
    ),
    community: (
      <div className="space-y-2">
        {[
          { n: "Janko T.", role: "EXPERT", t: "Hotové! Orbitec OK 76 program pre 304L DN85 sm hodil do Reálnych programov." },
          { n: "Peter M.", role: "SKÚSENÝ", t: "Ďakujem za tip s pulzom 1.2:0.5 — zvar je ako zo skla." },
          { n: "Sauberino 🤖", role: "AI", t: "Pri DN85 odporúčam zaviesť sektor 30° s back-purge 5–7 l/min." },
        ].map((m, i) => (
          <CommunityRow key={i} m={m} />
        ))}
      </div>
    ),
  },
  en: {
    ai: (
      <div className="space-y-3">
        <div className="bg-zinc-900 border border-zinc-800 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">You</div>
          <div className="text-zinc-200">I'm getting root porosity on stainless DN50, 316L, Axxair orbital. What now?</div>
        </div>
        <div className="bg-gold-950/20 border border-gold-700/40 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-gold-500 mb-1">🛠️ Sauberino</div>
          <div className="text-zinc-200 leading-relaxed">
            Ah, root porosity — the classic nerve-killer. Three usual suspects: 1) gas — moisture in the hose or dirty argon, 2) not enough back-purge — give it at least 5 l/min, 3) greasy material — acetone + lint-free wipe before welding...
          </div>
        </div>
      </div>
    ),
    community: (
      <div className="space-y-2">
        {[
          { n: "John T.", role: "EXPERT", t: "Done! Posted my Orbitec OK 76 program for 304L DN85 sm to Real Programs." },
          { n: "Peter M.", role: "EXPERIENCED", t: "Thanks for the 1.2:0.5 pulse tip — weld looks like glass." },
          { n: "Sauberino 🤖", role: "AI", t: "For DN85 I recommend a 30° sector with 5–7 l/min back-purge." },
        ].map((m, i) => (
          <CommunityRow key={i} m={m} />
        ))}
      </div>
    ),
  },
  cs: {
    ai: (
      <div className="space-y-3">
        <div className="bg-zinc-900 border border-zinc-800 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Ty</div>
          <div className="text-zinc-200">Mám póry v kořeni nerez DN50, 316L, orbitálka Axxair. Co s tím?</div>
        </div>
        <div className="bg-gold-950/20 border border-gold-700/40 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-gold-500 mb-1">🛠️ Sauberino</div>
          <div className="text-zinc-200 leading-relaxed">
            No tak — póry v kořeni, klasika která sežere nervy. Tři viníci, vždy ti samí: 1) plyn — vlhko v hadici nebo nečistý argon, 2) málo back-purge — dej aspoň 5 l/min, 3) mastný materiál — před svarem aceton a netkaný hadr...
          </div>
        </div>
      </div>
    ),
    community: (
      <div className="space-y-2">
        {[
          { n: "Honza T.", role: "EXPERT", t: "Hotovo! Orbitec OK 76 program pro 304L DN85 sm hodil do Reálných programů." },
          { n: "Petr M.", role: "ZKUŠENÝ", t: "Díky za tip s pulzem 1.2:0.5 — svar je jako ze skla." },
          { n: "Sauberino 🤖", role: "AI", t: "Při DN85 doporučuji sektor 30° s back-purge 5–7 l/min." },
        ].map((m, i) => (
          <CommunityRow key={i} m={m} />
        ))}
      </div>
    ),
  },
  de: {
    ai: (
      <div className="space-y-3">
        <div className="bg-zinc-900 border border-zinc-800 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Du</div>
          <div className="text-zinc-200">Ich habe Wurzelporosität bei Edelstahl DN50, 316L, Axxair Orbital. Was nun?</div>
        </div>
        <div className="bg-gold-950/20 border border-gold-700/40 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-gold-500 mb-1">🛠️ Sauberino</div>
          <div className="text-zinc-200 leading-relaxed">
            Ah, Wurzelporosität — der klassische Nervenkiller. Drei übliche Verdächtige: 1) Gas — Feuchtigkeit im Schlauch oder verschmutztes Argon, 2) zu wenig Back-Purge — gib mindestens 5 l/min, 3) fettiges Material — vor dem Schweißen Aceton + fusselfreies Tuch...
          </div>
        </div>
      </div>
    ),
    community: (
      <div className="space-y-2">
        {[
          { n: "Johann T.", role: "EXPERTE", t: "Erledigt! Mein Orbitec OK 76 Programm für 304L DN85 sm in Echte Programme gepostet." },
          { n: "Peter M.", role: "ERFAHREN", t: "Danke für den 1.2:0.5 Puls-Tipp — die Naht sieht aus wie Glas." },
          { n: "Sauberino 🤖", role: "KI", t: "Bei DN85 empfehle ich einen 30°-Sektor mit 5–7 l/min Back-Purge." },
        ].map((m, i) => (
          <CommunityRow key={i} m={m} />
        ))}
      </div>
    ),
  },
  pl: {
    ai: (
      <div className="space-y-3">
        <div className="bg-zinc-900 border border-zinc-800 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Ty</div>
          <div className="text-zinc-200">Mam pory w grani na nierdzewce DN50, 316L, orbitalka Axxair. Co z tym?</div>
        </div>
        <div className="bg-gold-950/20 border border-gold-700/40 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-gold-500 mb-1">🛠️ Sauberino</div>
          <div className="text-zinc-200 leading-relaxed">
            No tak — pory w grani, klasyka która zjada nerwy. Trzy winowajce, zawsze ci sami: 1) gaz — wilgoć w wężu albo brudny argon, 2) za mało back-purge — daj minimum 5 l/min, 3) tłusty materiał — przed spawaniem aceton i niepylna szmatka...
          </div>
        </div>
      </div>
    ),
    community: (
      <div className="space-y-2">
        {[
          { n: "Janek T.", role: "EKSPERT", t: "Gotowe! Program Orbitec OK 76 dla 304L DN85 sm wrzucony do Realnych programów." },
          { n: "Piotr M.", role: "DOŚWIADCZONY", t: "Dzięki za tip z pulsem 1.2:0.5 — spoina jak ze szkła." },
          { n: "Sauberino 🤖", role: "AI", t: "Przy DN85 polecam sektor 30° z back-purge 5–7 l/min." },
        ].map((m, i) => (
          <CommunityRow key={i} m={m} />
        ))}
      </div>
    ),
  },
  fr: {
    ai: (
      <div className="space-y-3">
        <div className="bg-zinc-900 border border-zinc-800 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Toi</div>
          <div className="text-zinc-200">J'ai de la porosité à la racine sur DN50 inox 316L, orbital Axxair. Comment faire ?</div>
        </div>
        <div className="bg-gold-950/20 border border-gold-700/40 p-3 text-sm">
          <div className="text-[10px] font-mono uppercase text-gold-500 mb-1">🛠️ Sauberino</div>
          <div className="text-zinc-200 leading-relaxed">
            Aïe — porosité racine, le classique qui bouffe les nerfs. Trois coupables, toujours les mêmes : 1) gaz — humidité dans le tuyau ou argon sale, 2) pas assez de back-purge — mets au moins 5 l/min, 3) matériau gras — avant soudage acétone et chiffon non-tissé...
          </div>
        </div>
      </div>
    ),
    community: (
      <div className="space-y-2">
        {[
          { n: "Janko T.", role: "EXPERT", t: "C'est fait ! Programme Orbitec OK 76 pour 304L DN85 ajouté dans Programmes Réels." },
          { n: "Peter M.", role: "SKÚSENÝ", t: "Merci pour l'astuce pulse 1.2:0.5 — soudure nickel chrome." },
          { n: "Sauberino 🤖", role: "AI", t: "Pour DN85 je recommande secteur 30° avec back-purge 5–7 l/min." },
        ].map((m, i) => (
          <CommunityRow key={i} m={m} />
        ))}
      </div>
    ),
  },
};

function CommunityRow({ m }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 p-3 text-sm flex items-start gap-3">
      <div className="w-9 h-9 bg-gold-600/20 border border-gold-700/30 flex items-center justify-center font-display font-black text-gold-400 text-sm">
        {m.n[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-bold text-sm">{m.n}</span>
          <span className="text-[9px] font-mono uppercase tracking-wider text-gold-500 border border-gold-700/40 px-1.5">{m.role}</span>
        </div>
        <div className="text-zinc-300 mt-1 text-xs leading-relaxed">{m.t}</div>
      </div>
    </div>
  );
}

export default function Tour() {
  const nav = useNavigate();
  const { lang } = useLang();
  const { user } = useAuth();
  const [step, setStep] = useState(0);

  const tr = TOUR_TR[lang] || TOUR_TR.en;
  const visuals = VISUALS_BY_LANG[lang] || VISUALS_BY_LANG.en;
  const totalSteps = tr.steps.length;
  const cur = { ...tr.steps[step], ...META[step] };
  if (cur.visualKey) cur.visual = visuals[cur.visualKey];
  if (step === 1) cur.visual = <VisualOrbital />;  // step 1 visual same in all languages
  const isLast = step === totalSteps - 1;
  const progress = ((step + 1) / totalSteps) * 100;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" && !isLast) setStep(step + 1);
      if (e.key === "ArrowLeft" && step > 0) setStep(step - 1);
      if (e.key === "Escape") nav("/");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, isLast, nav]);

  return (
    <div data-testid="tour-page" className="min-h-screen bg-[var(--wb-bg)] text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400">
            <Sparkles className="w-3.5 h-3.5 text-gold-500" />
            {tr.title}
            <span className="text-zinc-700">·</span>
            <span className="text-zinc-500">{step + 1} / {totalSteps}</span>
          </div>
          <button
            onClick={() => nav("/")}
            data-testid="tour-close"
            aria-label="Close"
            className="w-9 h-9 flex items-center justify-center text-zinc-500 hover:text-white border border-zinc-800 hover:border-gold-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="h-0.5 bg-zinc-900">
          <div
            className="h-full bg-gradient-to-r from-gold-600 to-amber-400 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 md:py-20">
        <StepCard step={cur} index={step} key={step} tr={tr} />

        <div className="mt-12 flex items-center justify-between gap-4 flex-wrap">
          <button
            onClick={() => setStep(step - 1)}
            disabled={step === 0}
            data-testid="tour-prev"
            className="wb-btn-ghost px-5 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            {tr.back}
          </button>

          <div className="flex items-center gap-1.5">
            {tr.steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                aria-label={`Step ${i + 1}`}
                data-testid={`tour-dot-${i}`}
                className={`h-1.5 transition-all ${
                  i === step ? "w-8 bg-gold-500" : "w-1.5 bg-zinc-700 hover:bg-zinc-500"
                }`}
              />
            ))}
          </div>

          {!isLast ? (
            <button
              onClick={() => setStep(step + 1)}
              data-testid="tour-next"
              className="wb-btn-primary px-6 py-3 text-sm inline-flex items-center gap-2"
            >
              {tr.next}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <Link
              to={user ? "/community" : "/register"}
              data-testid="tour-finish-cta"
              className="wb-btn-primary px-6 py-3 text-sm inline-flex items-center gap-2 !bg-gold-500 hover:!bg-gold-400"
            >
              <Rocket className="w-4 h-4" />
              {user ? tr.finishCommunity : tr.finishRegister}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function StepCard({ step, index, tr }) {
  const Icon = step.icon;
  return (
    <div data-testid={`tour-step-${index}`} className="wb-fade-in">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-14 h-14 bg-gold-500/10 border border-gold-600 flex items-center justify-center">
          <Icon className="w-7 h-7 text-gold-400" />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold-500">
            {step.tag}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">{tr.stepLabel} {index + 1}</div>
        </div>
      </div>

      <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-5">
        {step.titleA}<br/>
        <span className="wb-gold-text">{step.titleB}</span>
      </h1>

      <p className="text-lg md:text-xl text-zinc-300 leading-relaxed max-w-3xl">
        {step.subtitle}
      </p>

      {step.bullets && (
        <ul className="mt-8 space-y-3 max-w-2xl">
          {step.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
              <CheckCircle2 className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {step.showPromo && (
        <div className="mt-8 max-w-2xl">
          <LaunchPromoBanner />
        </div>
      )}

      {step.visual && (
        <div className="mt-10 border border-zinc-800 bg-gradient-to-br from-zinc-950 to-black p-6 md:p-8">
          {step.visual}
        </div>
      )}

      {step.tryLink && step.tryLabel && (
        <div className="mt-8">
          <Link
            to={step.tryLink}
            data-testid={`tour-try-${index}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-gold-600 text-gold-400 hover:bg-gold-500/10 text-xs font-mono uppercase tracking-widest transition-colors"
          >
            <Play className="w-3 h-3" /> {step.tryLabel}
          </Link>
        </div>
      )}
    </div>
  );
}
